import React, { useState, useEffect, useCallback } from 'react';
import Swal from 'sweetalert2';
import {
  obtenerHistorialNotificaciones,
  marcarNotificacionLeida,
  marcarTodasLeidasNotificaciones,
  eliminarNotificacion,
  eliminarNotificacionesLote,
  eliminarNotificacionesAntiguas,
  activarUsuario,
} from '../api/backend_v2';
import {
  BellIcon,
  BeakerIcon,
  TrashIcon,
  CheckCircleIcon,
  FunnelIcon,
  EyeIcon,
  ArrowPathIcon,
  CalendarIcon,
  ExclamationCircleIcon,
  BanknotesIcon,
  UserPlusIcon,
} from '@heroicons/react/24/outline';

const GestionNotificaciones = ({ onViewChange, usuario }) => {
  const rolUsuario = usuario?.rol || (() => {
    try {
      return JSON.parse(localStorage.getItem('usuario') || '{}')?.rol;
    } catch (_) {
      return null;
    }
  })();
  const esSuperAdmin = rolUsuario === 'superadmin';

  const [notificaciones, setNotificaciones] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busqueda, setBusqueda] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('todas');
  const [filtroEstado, setFiltroEstado] = useState('todas'); // 'todas', 'sin_leer', 'leidas'
  const [seleccionadas, setSeleccionadas] = useState([]);
  const [notifSeleccionada, setNotifSeleccionada] = useState(null); // Para modal de detalles

  const cargarNotificaciones = useCallback(async () => {
    try {
      setLoading(true);
      let sinLeerParam = null;
      if (filtroEstado === 'sin_leer') sinLeerParam = true;
      if (filtroEstado === 'leidas') sinLeerParam = false;

      const res = await obtenerHistorialNotificaciones({
        sinLeer: sinLeerParam,
        tipo: filtroTipo === 'todas' ? null : filtroTipo,
        buscar: busqueda,
        limite: 100,
        pagina: 1,
      });

      const notifsRecibidas = (res.notificaciones || []).filter(
        (n) => esSuperAdmin || n.tipo !== 'nuevo_usuario'
      );
      setNotificaciones(notifsRecibidas);
      setTotal(esSuperAdmin ? (res.total || 0) : notifsRecibidas.length);
      setSeleccionadas([]);
    } catch (err) {
      console.error('Error cargando notificaciones:', err);
      Swal.fire('Error', 'No se pudieron cargar las notificaciones', 'error');
    } finally {
      setLoading(false);
    }
  }, [filtroEstado, filtroTipo, busqueda, esSuperAdmin]);

  useEffect(() => {
    cargarNotificaciones();
  }, [cargarNotificaciones]);

  // Selección múltiple
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSeleccionadas(notificaciones.map((n) => n.id));
    } else {
      setSeleccionadas([]);
    }
  };

  const handleToggleSelect = (id) => {
    setSeleccionadas((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  // Acciones individuales y en lote
  const handleMarcarLeidaItem = async (id) => {
    try {
      await marcarNotificacionLeida(id);
      setNotificaciones((prev) =>
        prev.map((n) => (n.id === id ? { ...n, leida: true } : n))
      );
    } catch (err) {
      Swal.fire('Error', 'No se pudo marcar como leída', 'error');
    }
  };

  const handleEliminarItem = async (id) => {
    const confirm = await Swal.fire({
      title: '¿Eliminar notificación?',
      text: 'Esta acción no se puede deshacer.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
    });

    if (confirm.isConfirmed) {
      try {
        await eliminarNotificacion(id);
        setNotificaciones((prev) => prev.filter((n) => n.id !== id));
        setTotal((prev) => prev - 1);
        setSeleccionadas((prev) => prev.filter((item) => item !== id));
        Swal.fire({
          icon: 'success',
          title: 'Notificación eliminada',
          timer: 1200,
          showConfirmButton: false,
        });
      } catch (err) {
        Swal.fire('Error', 'No se pudo eliminar la notificación', 'error');
      }
    }
  };

  const handleEliminarSeleccionadas = async () => {
    if (seleccionadas.length === 0) return;

    const confirm = await Swal.fire({
      title: `¿Eliminar ${seleccionadas.length} notificaciones?`,
      text: 'Se eliminarán permanentemente las notificaciones seleccionadas.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      confirmButtonText: `Sí, eliminar ${seleccionadas.length}`,
      cancelButtonText: 'Cancelar',
    });

    if (confirm.isConfirmed) {
      try {
        const res = await eliminarNotificacionesLote(seleccionadas);
        Swal.fire({
          icon: 'success',
          title: 'Eliminación completada',
          text: res.mensaje || `${seleccionadas.length} notificaciones eliminadas.`,
        });
        cargarNotificaciones();
      } catch (err) {
        Swal.fire('Error', 'No se pudo realizar la eliminación masiva', 'error');
      }
    }
  };

  const handleEliminarAntiguas = async () => {
    const confirm = await Swal.fire({
      title: '¿Eliminar notificaciones de más de 30 días?',
      html: 'Se borrarán de la base de datos todas las notificaciones generadas <b>hace más de 1 mes</b>.<br><br><i>(Nota: el sistema borra automáticamente a los 60 días).</i>',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      confirmButtonText: 'Sí, borrar notificaciones de +30 días',
      cancelButtonText: 'Cancelar',
    });

    if (confirm.isConfirmed) {
      try {
        const res = await eliminarNotificacionesAntiguas(30);
        Swal.fire({
          icon: 'success',
          title: 'Limpieza de antigüedad completada',
          text: res.mensaje,
        });
        cargarNotificaciones();
      } catch (err) {
        Swal.fire('Error', 'No se pudo ejecutar la limpieza por antigüedad', 'error');
      }
    }
  };

  const handleMarcarTodasLeidas = async () => {
    try {
      await marcarTodasLeidasNotificaciones();
      setNotificaciones((prev) => prev.map((n) => ({ ...n, leida: true })));
      Swal.fire({
        icon: 'success',
        title: 'Todas las notificaciones marcadas como leídas',
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (err) {
      Swal.fire('Error', 'No se pudieron marcar todas como leídas', 'error');
    }
  };

  // Métricas
  const noLeidasCount = notificaciones.filter((n) => !n.leida).length;
  const labCount = notificaciones.filter(
    (n) => n.tipo === 'laboratorio_alertas' || n.tipo?.includes('laboratorio')
  ).length;
  const finanzasCount = notificaciones.filter(
    (n) => n.tipo === 'pagos_alerta' || n.tipo === 'pago_dinamico'
  ).length;

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-6 rounded-xl shadow-sm border border-gray-100">
        <div>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg text-blue-600">
              <BellIcon className="h-6 w-6" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Gestión e Historial de Notificaciones</h1>
              <p className="text-sm text-gray-500 mt-0.5">
                Consulta, filtra y administra las notificaciones del sistema o limpia registros antiguos.
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={cargarNotificaciones}
            className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium flex items-center gap-1.5 transition-colors"
            title="Recargar listado"
          >
            <ArrowPathIcon className="h-4 w-4" />
            Actualizar
          </button>
          <button
            onClick={handleMarcarTodasLeidas}
            className="px-3 py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg text-sm font-medium flex items-center gap-1.5 transition-colors"
          >
            <CheckCircleIcon className="h-4 w-4" />
            Marcar todas leídas
          </button>
          <button
            onClick={handleEliminarAntiguas}
            className="px-3 py-2 bg-amber-50 hover:bg-amber-100 text-amber-800 rounded-lg text-sm font-medium flex items-center gap-1.5 transition-colors"
          >
            <CalendarIcon className="h-4 w-4 text-amber-600" />
            Borrar del mes anterior (+30 días)
          </button>
        </div>
      </div>

      {/* Cards de Métricas */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 flex items-center gap-4">
          <div className="p-3 bg-blue-50 text-blue-600 rounded-lg">
            <BellIcon className="h-6 w-6" />
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Total visibles</p>
            <p className="text-2xl font-bold text-gray-900">{total}</p>
          </div>
        </div>

        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 flex items-center gap-4">
          <div className="p-3 bg-red-50 text-red-600 rounded-lg">
            <ExclamationCircleIcon className="h-6 w-6" />
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Sin leer</p>
            <p className="text-2xl font-bold text-red-600">{noLeidasCount}</p>
          </div>
        </div>

        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 flex items-center gap-4">
          <div className="p-3 bg-amber-50 text-amber-600 rounded-lg">
            <BeakerIcon className="h-6 w-6" />
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Laboratorio</p>
            <p className="text-2xl font-bold text-amber-700">{labCount}</p>
          </div>
        </div>

        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 flex items-center gap-4">
          <div className="p-3 bg-emerald-50 text-emerald-600 rounded-lg">
            <BanknotesIcon className="h-6 w-6" />
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Finanzas / Pagos</p>
            <p className="text-2xl font-bold text-emerald-700">{finanzasCount}</p>
          </div>
        </div>
      </div>

      {/* Barra de Filtros y Selección */}
      <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 flex flex-col md:flex-row items-stretch md:items-center justify-between gap-4">
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 flex-1">
          {/* Búsqueda */}
          <div className="relative flex-1">
            <input
              type="text"
              placeholder="Buscar por cliente, mensaje o tipo..."
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <FunnelIcon className="h-4 w-4 text-gray-400 absolute left-3 top-2.5" />
          </div>

          {/* Filtro por Tipo */}
          <select
            value={filtroTipo}
            onChange={(e) => setFiltroTipo(e.target.value)}
            className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
          >
            <option value="todas">Todos los tipos</option>
            <option value="laboratorio_alertas">Laboratorio</option>
            <option value="pagos_alerta">Finanzas / Pagos</option>
            {esSuperAdmin && <option value="nuevo_usuario">Nuevos Usuarios</option>}
          </select>

          {/* Filtro por Lectura */}
          <select
            value={filtroEstado}
            onChange={(e) => setFiltroEstado(e.target.value)}
            className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
          >
            <option value="todas">Todas (Leídas y No leídas)</option>
            <option value="sin_leer">Solo sin leer</option>
            <option value="leidas">Solo leídas</option>
          </select>
        </div>

        {/* Botón de eliminación en lote */}
        {seleccionadas.length > 0 && (
          <button
            onClick={handleEliminarSeleccionadas}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-semibold flex items-center justify-center gap-2 shadow-sm transition-colors"
          >
            <TrashIcon className="h-4 w-4" />
            Eliminar seleccionadas ({seleccionadas.length})
          </button>
        )}
      </div>

      {/* Tabla de Notificaciones */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="py-12 text-center text-gray-500">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-3"></div>
            Cargando notificaciones...
          </div>
        ) : notificaciones.length === 0 ? (
          <div className="py-12 text-center text-gray-500">
            <BellIcon className="h-12 w-12 text-gray-300 mx-auto mb-2" />
            <p className="text-base font-semibold text-gray-700">Sin notificaciones</p>
            <p className="text-xs text-gray-500 mt-1">
              No se encontraron resultados para los filtros seleccionados.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200 text-xs uppercase font-semibold text-gray-600">
                  <th className="p-3 w-10 text-center">
                    <input
                      type="checkbox"
                      checked={seleccionadas.length === notificaciones.length && notificaciones.length > 0}
                      onChange={handleSelectAll}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  </th>
                  <th className="p-3">Tipo</th>
                  <th className="p-3">Mensaje / Detalle</th>
                  <th className="p-3">Fecha</th>
                  <th className="p-3 text-center">Estado</th>
                  <th className="p-3 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 text-sm">
                {notificaciones.map((n) => {
                  const esLab =
                    n.tipo === 'laboratorio_alertas' ||
                    n.tipo?.includes('laboratorio') ||
                    n.titulo?.toLowerCase().includes('laboratorio');
                  const esPago =
                    n.tipo === 'pagos_alerta' ||
                    n.tipo === 'pago_dinamico' ||
                    n.tipo?.includes('pago');
                  const esNuevoUsuario =
                    n.tipo === 'nuevo_usuario' ||
                    n.titulo?.toLowerCase().includes('nuevo usuario');
                  const datos = n.datos || {};
                  const isChecked = seleccionadas.includes(n.id);

                  return (
                    <tr
                      key={n.id}
                      className={`hover:bg-gray-50 transition-colors ${
                        !n.leida ? (esLab ? 'bg-amber-50/50' : 'bg-blue-50/50') : ''
                      } ${isChecked ? 'bg-blue-100/40' : ''}`}
                    >
                      <td className="p-3 text-center">
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => handleToggleSelect(n.id)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                      </td>

                      <td className="p-3 whitespace-nowrap">
                        {esLab ? (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-amber-100 text-amber-800">
                            <BeakerIcon className="h-3.5 w-3.5" />
                            Laboratorio
                          </span>
                        ) : esPago ? (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-blue-100 text-blue-800">
                            <BanknotesIcon className="h-3.5 w-3.5" />
                            Finanzas
                          </span>
                        ) : esNuevoUsuario ? (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-purple-100 text-purple-800">
                            <UserPlusIcon className="h-3.5 w-3.5" />
                            Nuevo Usuario
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-700">
                            <BellIcon className="h-3.5 w-3.5" />
                            {n.tipo || 'Sistema'}
                          </span>
                        )}
                      </td>

                      <td className="p-3 max-w-md">
                        <p className="font-medium text-gray-900 leading-snug">
                          {n.mensaje || n.titulo || (datos.tipo_anteojo ? `${datos.tipo_anteojo} para ${datos.cliente_nombre}` : 'Notificación')}
                        </p>
                        {(datos.cliente_nombre || datos.nro_sobre || datos.cliente_telefono) && (
                          <div className="flex items-center gap-2 text-xs text-gray-500 mt-1 flex-wrap">
                            {datos.cliente_nombre && (
                              <span className="font-semibold text-gray-700">
                                👤 {datos.cliente_nombre}
                              </span>
                            )}
                            {datos.nro_sobre && (
                              <span className="bg-gray-100 px-1.5 py-0.5 rounded text-gray-600 font-mono text-[11px]">
                                Sobre #{datos.nro_sobre}
                              </span>
                            )}
                            {datos.cliente_telefono && (
                              <span>📞 {datos.cliente_telefono}</span>
                            )}
                            {datos.dias_laboratorio && (
                              <span className="text-red-600 font-semibold">
                                ⏱️ {datos.dias_laboratorio} días en lab
                              </span>
                            )}
                          </div>
                        )}
                      </td>

                      <td className="p-3 text-xs text-gray-500 whitespace-nowrap">
                        {n.fecha_creacion ? (
                          <div>
                            <p className="font-medium text-gray-700">
                              {new Date(n.fecha_creacion).toLocaleDateString('es-AR')}
                            </p>
                            <p className="text-[10px] text-gray-400">
                              {new Date(n.fecha_creacion).toLocaleTimeString('es-AR', {
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </p>
                          </div>
                        ) : (
                          '-'
                        )}
                      </td>

                      <td className="p-3 text-center whitespace-nowrap">
                        {n.leida ? (
                          <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                            <CheckCircleIcon className="h-4 w-4 text-emerald-500" />
                            Leída
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs font-bold text-red-600 bg-red-50 px-2 py-0.5 rounded-full">
                            <span className="h-2 w-2 rounded-full bg-red-500"></span>
                            Sin leer
                          </span>
                        )}
                      </td>

                      <td className="p-3 text-right whitespace-nowrap">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => setNotifSeleccionada(n)}
                            className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                            title="Ver detalles completos"
                          >
                            <EyeIcon className="h-4 w-4" />
                          </button>
                          {!n.leida && (
                            <button
                              onClick={() => handleMarcarLeidaItem(n.id)}
                              className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded-md transition-colors"
                              title="Marcar como leída"
                            >
                              <CheckCircleIcon className="h-4 w-4" />
                            </button>
                          )}
                          <button
                            onClick={() => handleEliminarItem(n.id)}
                            className="p-1.5 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                            title="Eliminar notificación"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal de Detalle */}
      {notifSeleccionada && (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-50 z-[2000] flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full overflow-hidden border border-gray-100 animate-fade-in">
            <div className="p-4 sm:p-6 border-b border-gray-100 flex items-center justify-between bg-gray-50">
              <div className="flex items-center gap-2">
                <BeakerIcon className="h-5 w-5 text-amber-600" />
                <h3 className="font-bold text-gray-900 text-lg">
                  {notifSeleccionada.titulo || 'Detalles de Notificación'}
                </h3>
              </div>
              <button
                onClick={() => setNotifSeleccionada(null)}
                className="text-gray-400 hover:text-gray-600 text-lg font-bold p-1"
              >
                ✕
              </button>
            </div>

            <div className="p-4 sm:p-6 space-y-4 max-h-[70vh] overflow-y-auto text-sm">
              <div className="bg-blue-50/70 p-3 rounded-lg text-blue-950 font-medium leading-relaxed">
                {notifSeleccionada.mensaje || 'Sin mensaje descriptivo.'}
              </div>

              {/* Atributos del JSON */}
              {notifSeleccionada.datos && (
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 space-y-2">
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                    Metadatos y atributos
                  </p>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    {Object.entries(notifSeleccionada.datos).map(([k, v]) => (
                      <div key={k} className="flex flex-col bg-white p-2 rounded border border-gray-100">
                        <span className="text-[10px] font-semibold text-gray-400 uppercase">
                          {k.replace(/_/g, ' ')}
                        </span>
                        <span className="font-bold text-gray-800 break-words">
                          {typeof v === 'object' ? JSON.stringify(v) : String(v)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between text-xs text-gray-500 border-t pt-3">
                <span>
                  Fecha creación:{' '}
                  {notifSeleccionada.fecha_creacion
                    ? new Date(notifSeleccionada.fecha_creacion).toLocaleString('es-AR')
                    : '-'}
                </span>
                <span>ID: {notifSeleccionada.id}</span>
              </div>
            </div>

            <div className="p-4 bg-gray-50 border-t border-gray-100 flex justify-end gap-2">
              {notifSeleccionada.tipo === 'laboratorio_alertas' && (
                <button
                  onClick={() => {
                    setNotifSeleccionada(null);
                    if (onViewChange) onViewChange('anteojos-laboratorio');
                  }}
                  className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-xs font-bold transition-colors"
                >
                  Ir a En Laboratorio
                </button>
              )}
              {esSuperAdmin && notifSeleccionada.tipo === 'nuevo_usuario' && (
                <button
                  onClick={async () => {
                    const datos = notifSeleccionada.datos || {};
                    try {
                      await activarUsuario(datos.usuario_id, datos.optica_id);
                      await handleMarcarLeidaItem(notifSeleccionada.id);
                      setNotifSeleccionada(null);
                      Swal.fire({
                        icon: 'success',
                        title: '¡Usuario activado!',
                        text: `El usuario ${datos.nombre_completo || ''} ha sido activado exitosamente.`,
                        timer: 2000,
                        showConfirmButton: false,
                      });
                      cargarNotificaciones();
                    } catch (err) {
                      Swal.fire({
                        icon: 'error',
                        title: 'Error al activar',
                        text: err.message || 'No se pudo activar el usuario',
                      });
                    }
                  }}
                  className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-bold transition-colors"
                >
                  Activar Usuario
                </button>
              )}
              <button
                onClick={() => setNotifSeleccionada(null)}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg text-xs font-semibold transition-colors"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GestionNotificaciones;
